import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, Edit3, MapPin, Heart, Upload, ShieldCheck, ShieldAlert, Sparkles, Camera, ShieldOff, Trash2, AlertOctagon, FileText, Gavel, MessageSquare, BarChart3, Plus, Star, X, Bell, Crown, Bus, CheckCircle2, Share2, Copy, Users, QrCode, Trophy, ChevronRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { CLUBS } from "@/data/clubs";
import { labelForConnection } from "@/data/connections";
import { labelForFanType } from "@/data/fanTypes";
import { isPushSupported, currentPermission, subscribePush, unsubscribePush, sendTestPush } from "@/lib/push";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import BottomNav from "@/components/app/BottomNav";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";

const clubColor = (n) => CLUBS.find(c => c.name === n)?.color || "#152A55";
const initialsOf = (name) => (name || "?").split(" ").map(w => w[0]).slice(0,2).join("").toUpperCase();
const photoSrc = (url) => (url && url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url);
const MAX_PHOTOS = 6;

export default function Profile() {
  const { user, logout, refresh } = useAuth();
  const nav = useNavigate();
  const p = user?.profile || {};
  const conn = (p.connection_types || []).map(labelForConnection);
  const doLogout = async () => { await logout(); nav("/"); };

  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const photos = (p.photos && p.photos.length ? p.photos : (p.photo_url ? [p.photo_url] : []));
  const uploadPhoto = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if ((photos?.length || 0) >= MAX_PHOTOS) {
      toast.error(`Max ${MAX_PHOTOS} photos. Remove one first.`);
      e.target.value = "";
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      await api.post("/upload/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refresh();
      toast.success("Photo uploaded");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed"); }
    finally { setUploading(false); e.target.value = ""; }
  };
  const removePhoto = async (index) => {
    try {
      await api.delete("/upload/photo", { data: { index } });
      await refresh();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not remove photo"); }
  };
  const makePrimary = async (index) => {
    try {
      await api.post("/upload/photo/primary", { index });
      await refresh();
      toast.success("Primary photo updated");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not set primary"); }
  };

  const [verifyOpen, setVerifyOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const isVerified = !!p.photo_verified;
  // Deep-link support: /profile?verify=1 opens the verify modal on mount.
  // Used by the Discover "get verified" banner so users can go straight from prompt → action.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("verify") === "1" && !isVerified) {
      setVerifyOpen(true);
      try { window.history.replaceState({}, "", "/profile"); } catch { /* ignore */ }
    }
  // eslint-disable-next-line
  }, []);
  // Re-verification nudge: verified users whose badge is > 180 days old are
  // gently prompted to re-verify. Cosmetic only — doesn't strip the badge.
  const REVERIFY_DAYS = 180;
  const verifiedAt = p.verification_at ? new Date(p.verification_at) : null;
  const daysSinceVerify = verifiedAt && !isNaN(verifiedAt.getTime())
    ? Math.floor((Date.now() - verifiedAt.getTime()) / (1000 * 60 * 60 * 24))
    : null;
  const needsReverify = isVerified && daysSinceVerify !== null && daysSinceVerify >= REVERIFY_DAYS;

  return (
    <div className="min-h-screen bg-footy-bg text-white pb-24 grain">
      <div className="relative h-52" style={{ background: `linear-gradient(160deg, ${clubColor(p.club)}, #061022)` }}>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-footy-bg/90" />
        <button onClick={doLogout} data-testid="logout-btn"
          className="absolute top-4 right-4 text-slate-200 hover:text-white p-2 rounded-full bg-white/10 hover:bg-white/20">
          <LogOut size={18} />
        </button>
      </div>

      <div className="max-w-md mx-auto px-5 -mt-16 relative">
        <div className="relative mx-auto w-32 h-32">
          <div className="w-32 h-32 rounded-full border-4 overflow-hidden bg-footy-card" style={{ borderColor: clubColor(p.club) }}>
            {p.photo_url ? <img src={photoSrc(p.photo_url)} alt="" className="w-full h-full object-cover" data-testid="profile-photo" /> :
              <div className="w-full h-full flex items-center justify-center font-display text-6xl"
                   style={{ background: `linear-gradient(160deg, ${clubColor(p.club)}, #061022)` }}>{initialsOf(user?.name)}</div>}
          </div>
          {isVerified && (
            <div className="absolute -bottom-1 -right-1 h-9 rounded-full bg-emerald-500 border-4 border-footy-bg flex items-center justify-center gap-1 px-2 text-[10px] font-bold uppercase tracking-widest text-white" title="Verified Supporter" data-testid="verified-badge">
              <ShieldCheck size={12} strokeWidth={3} /> Verified
            </div>
          )}
          <button onClick={()=>fileRef.current?.click()} disabled={uploading}
            className="absolute bottom-0 left-0 w-9 h-9 rounded-full bg-footy-red border-4 border-footy-bg flex items-center justify-center hover:bg-footy-redHover"
            data-testid="upload-photo-btn" title="Upload photo">
            {uploading ? <Sparkles size={14} className="animate-spin" /> : <Upload size={14} />}
          </button>
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={uploadPhoto} data-testid="photo-file-input" />
        </div>

        <div className="text-center mt-4">
          <h1 className="font-display text-4xl uppercase tracking-tight leading-none">{user?.name} <span className="text-footy-red">{p.age}</span></h1>
          <div className="mt-2 text-sm text-slate-400 flex items-center justify-center gap-1"><MapPin size={14} />{p.location || "—"}</div>
          <div className="mt-3 inline-block text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-full text-white"
               style={{ backgroundColor: clubColor(p.club) }}>Supports {p.club || "—"}</div>
        </div>

        {/* Grounds Book */}
        <GroundsBookCard />

        {/* Football Passport */}
        <FootballPassport />

        {/* Verification card */}
        <div className={`mt-6 footy-card p-5 border ${needsReverify ? "!border-amber-500/50" : isVerified ? "!border-emerald-500/40" : "!border-footy-red/40"}`} data-testid="verification-card">
          <div className="flex items-center gap-3">
            <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${needsReverify ? "bg-amber-500/15 text-amber-400" : isVerified ? "bg-emerald-500/20 text-emerald-400" : "bg-footy-red/15 text-footy-red"}`}>
              {isVerified ? <ShieldCheck size={22} /> : <ShieldAlert size={22} />}
            </div>
            <div className="flex-1">
              <div className="font-display text-2xl uppercase tracking-wide leading-none">
                {needsReverify ? "Time to re-verify" : isVerified ? "Verified supporter" : "Verify your profile"}
              </div>
              <div className="text-sm text-slate-400 mt-1">
                {needsReverify
                  ? `Your verification is ${daysSinceVerify} days old. Refresh it to keep your badge trustworthy.`
                  : isVerified
                    ? "Others know it's really you."
                    : "A live selfie earns you a trust badge. Takes 20 seconds."}
              </div>
            </div>
          </div>
          {(!isVerified || needsReverify) && p.photo_url && (
            <>
              {!isVerified && <VerifySocialProof />}
              <button onClick={()=>setVerifyOpen(true)}
                className={needsReverify ? "footy-btn-secondary w-full mt-4 !border-amber-500/60 !text-amber-300" : "footy-btn-primary w-full mt-4"}
                data-testid={needsReverify ? "reverify-btn" : "start-verify-btn"}>
                <Camera size={16} /> {needsReverify ? "Re-verify now" : "Start verification"}
              </button>
            </>
          )}
          {!p.photo_url && <p className="text-xs text-slate-500 mt-4">Upload a profile photo first — then verify with a live selfie.</p>}
        </div>

        {/* Photo gallery — up to 6 photos */}
        <div className="mt-4 footy-card p-5" data-testid="profile-photo-gallery">
          <div className="flex items-center justify-between mb-3">
            <p className="footy-label !mb-0">My photos <span className="text-slate-500 text-xs normal-case tracking-normal">({photos.length}/{MAX_PHOTOS})</span></p>
            {photos.length < MAX_PHOTOS && (
              <button onClick={()=>fileRef.current?.click()} disabled={uploading}
                className="text-xs font-bold uppercase tracking-widest text-footy-red hover:text-footy-redHover inline-flex items-center gap-1 disabled:opacity-50"
                data-testid="add-photo-btn">
                {uploading ? <Sparkles size={12} className="animate-spin" /> : <Plus size={12} />} Add
              </button>
            )}
          </div>
          <ProfilePhotoGrid photos={photos} onRemove={removePhoto} onSetPrimary={makePrimary} />
          <p className="text-xs text-slate-500 mt-3">First photo is your primary — that&apos;s what others see first. Changing it resets your verification badge.</p>
        </div>

        <div className="mt-4 footy-card p-5">
          <p className="footy-label">About</p>
          <p className="text-sm text-slate-200 leading-relaxed">{p.bio || "No bio yet."}</p>
        </div>
        <div className="mt-4 footy-card p-5">
          <p className="footy-label">Looking for</p>
          <div className="flex flex-wrap gap-2">
            {conn.length ? conn.map(c => <span key={c} className="footy-chip-active !cursor-default inline-flex items-center gap-1"><Heart size={12} /> {c}</span>) : <span className="text-sm text-slate-400">Not set</span>}
          </div>
        </div>

        <div className="mt-4 footy-card p-5" data-testid="profile-fan-type-card">
          <p className="footy-label">Fan type</p>
          {(p.fan_type || []).length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {p.fan_type.map(ft => (
                <span key={ft} className="text-[11px] uppercase tracking-widest font-bold px-2.5 py-1 rounded-full bg-yellow-500/15 text-yellow-200 border border-yellow-500/30" data-testid={`profile-fan-type-${ft}`}>
                  {labelForFanType(ft)}
                </span>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-400">Not set — <button onClick={() => nav("/onboarding")} className="text-footy-red hover:underline">add one via Edit profile</button>. Armchair loyal counts too.</p>
          )}
        </div>

        <button onClick={()=>nav("/onboarding")} className="footy-btn-secondary mt-6 w-full" data-testid="edit-profile">
          <Edit3 size={16} /> Edit profile
        </button>

        {user?.role === "admin" && (
          <div className="grid grid-cols-3 gap-2 mt-3">
            <Link to="/admin/reports" className="footy-btn-secondary !px-2 text-xs" data-testid="admin-shortcut">
              <Gavel size={14} /> Reports
            </Link>
            <Link to="/admin/feedback" className="footy-btn-secondary !px-2 text-xs" data-testid="admin-feedback-shortcut">
              <MessageSquare size={14} /> Feedback
            </Link>
            <Link to="/admin/analytics" className="footy-btn-secondary !px-2 text-xs" data-testid="admin-analytics-shortcut">
              <BarChart3 size={14} /> Analytics
            </Link>
          </div>
        )}

        <BlockedList />

        {/* Legal & account controls */}
        <div className="mt-6 footy-card p-5">
          <p className="footy-label">Legal</p>
          <div className="grid grid-cols-3 gap-2">
            <Link to="/terms" className="footy-chip inline-flex items-center gap-1 justify-center" data-testid="profile-terms"><FileText size={12} /> Terms</Link>
            <Link to="/privacy" className="footy-chip inline-flex items-center gap-1 justify-center" data-testid="profile-privacy"><FileText size={12} /> Privacy</Link>
            <Link to="/guidelines" className="footy-chip inline-flex items-center gap-1 justify-center" data-testid="profile-guidelines"><FileText size={12} /> Rules</Link>
          </div>
        </div>

        <NotificationsCard user={user} refresh={refresh} />

        <button onClick={()=>setDeleteOpen(true)} className="mt-6 w-full py-3 rounded-full border border-footy-red/40 text-footy-red text-sm font-bold uppercase tracking-widest hover:bg-footy-red/10" data-testid="delete-account-btn">
          <Trash2 size={14} className="inline mr-2" /> Delete my account
        </button>
      </div>

      {verifyOpen && <VerifyModal onClose={()=>setVerifyOpen(false)} onDone={async()=>{ await refresh(); setVerifyOpen(false); }} />}
      {deleteOpen && <DeleteAccountModal onClose={()=>setDeleteOpen(false)} onDeleted={async ()=>{ await logout(); nav("/", { replace: true }); }} />}
      <BottomNav />
    </div>
  );
}

function DeleteAccountModal({ onClose, onDeleted }) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!confirm) { setError("Tick the box to confirm this is permanent"); return; }
    if (!password) { setError("Enter your password"); return; }
    setBusy(true);
    try {
      await api.delete("/profile/me", { data: { password, confirm } });
      toast.success("Account deleted. Goodbye — thanks for trying Footymatch.");
      await onDeleted();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Delete failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur p-4" onClick={onClose} data-testid="delete-modal">
      <div className="w-full max-w-sm bg-footy-card border border-footy-red/50 rounded-2xl p-6" onClick={(e)=>e.stopPropagation()}>
        <div className="flex items-center gap-3">
          <AlertOctagon className="text-footy-red" />
          <h2 className="font-display text-2xl uppercase">Delete account</h2>
        </div>
        <p className="text-sm text-slate-400 mt-3">
          This <span className="text-white font-bold">permanently</span> removes your profile, photos, matches, messages, fixtures and RSVPs. It cannot be undone.
        </p>
        <form onSubmit={submit} className="mt-5 space-y-4">
          <div>
            <label className="footy-label">Confirm your password</label>
            <input type="password" value={password} onChange={(e)=>setPassword(e.target.value)}
              className="footy-input" data-testid="delete-password" autoFocus />
          </div>
          <label className="flex items-start gap-3 text-sm text-slate-300 cursor-pointer">
            <input type="checkbox" checked={confirm} onChange={(e)=>setConfirm(e.target.checked)}
              className="w-5 h-5 mt-0.5 accent-footy-red flex-none" data-testid="delete-confirm-checkbox" />
            <span>I understand this deletion is permanent and cannot be recovered.</span>
          </label>
          {error && <div className="text-sm text-footy-red bg-footy-red/10 border border-footy-red/30 rounded-lg px-3 py-2" data-testid="delete-error">{error}</div>}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="footy-btn-secondary flex-1" data-testid="delete-cancel">Cancel</button>
            <button type="submit" disabled={busy || !confirm || !password}
              className="flex-1 py-3 rounded-full bg-footy-red text-white font-bold text-sm uppercase tracking-widest hover:bg-footy-redHover disabled:opacity-50"
              data-testid="delete-submit">
              {busy ? "Deleting..." : "Delete forever"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function BlockedList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/blocks"); setItems(data); }
    catch { /* ignore */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const unblock = async (id) => {
    setBusyId(id);
    try {
      await api.post("/unblock", { target_id: id });
      setItems(prev => prev.filter(i => i.id !== id));
      toast.success("Unblocked");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Unblock failed");
    } finally { setBusyId(null); }
  };

  if (loading || items.length === 0) return null;

  return (
    <div className="mt-6 footy-card p-5" data-testid="blocked-list">
      <div className="flex items-center gap-2 mb-3">
        <ShieldOff size={16} className="text-footy-red" />
        <p className="footy-label !mb-0">Blocked ({items.length})</p>
      </div>
      <div className="space-y-2">
        {items.map(b => (
          <div key={b.id} className="flex items-center gap-3 py-2" data-testid={`blocked-${b.id}`}>
            <div className="w-10 h-10 rounded-full overflow-hidden bg-white/5 flex items-center justify-center text-slate-400 text-xs font-bold">
              {b.name?.[0]?.toUpperCase() || "?"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate">{b.name}</div>
              <div className="text-xs text-slate-500 truncate">Supports {b.club || "—"}</div>
            </div>
            <button onClick={()=>unblock(b.id)} disabled={busyId === b.id}
              className="text-xs font-bold uppercase tracking-widest text-footy-red hover:text-footy-redHover disabled:opacity-50"
              data-testid={`unblock-${b.id}`}>
              {busyId === b.id ? "..." : "Unblock"}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function VerifyModal({ onClose, onDone }) {
  const [prompt, setPrompt] = useState(null);
  const [status, setStatus] = useState("idle"); // idle | uploading | verified | rejected
  const [reason, setReason] = useState("");
  const inputRef = useRef(null);

  useEffect(() => { (async () => {
    try { const { data } = await api.get("/verify/prompt"); setPrompt(data); }
    catch { toast.error("Could not load a verification prompt"); }
  })(); }, []);

  const submit = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setStatus("uploading");
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post(`/verify/photo?prompt=${encodeURIComponent(prompt.label)}`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setReason(data.reason || "");
      if (data.verified) { setStatus("verified"); toast.success("Verified! Your badge is now live."); setTimeout(onDone, 1400); }
      else { setStatus("rejected"); }
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Verification failed"); setStatus("idle"); }
    finally { e.target.value = ""; }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-footy-bg/85 backdrop-blur p-0 sm:p-6" onClick={onClose} data-testid="verify-modal">
      <div className="w-full max-w-md bg-footy-card border border-white/10 sm:rounded-2xl rounded-t-2xl p-6 pb-[calc(1.5rem+env(safe-area-inset-bottom))] max-h-[92vh] overflow-y-auto" onClick={(e)=>e.stopPropagation()}>
        <div className="text-center">
          <p className="text-xs uppercase tracking-[0.25em] text-footy-red font-bold">Photo verification</p>
          <h2 className="font-display text-3xl uppercase mt-2">Live selfie challenge</h2>
        </div>
        {!prompt ? <p className="text-center text-slate-400 py-8">Loading…</p> : (
          <>
            <div className="mt-6 p-6 rounded-2xl bg-white/5 border border-white/10 text-center">
              <div className="text-6xl">{prompt.emoji}</div>
              <p className="mt-3 text-lg font-semibold">{prompt.label}</p>
              <p className="text-xs text-slate-400 mt-1">Snap a clear selfie doing exactly this.</p>
            </div>
            {status === "rejected" && (
              <div className="mt-4 p-3 rounded-lg bg-footy-red/10 border border-footy-red/40 text-sm text-footy-red" data-testid="verify-rejected">
                Didn't match — try again with clear lighting and the exact gesture.
                {reason && <div className="text-xs text-slate-400 mt-1">{reason}</div>}
              </div>
            )}
            {status === "verified" && (
              <div className="mt-4 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/40 text-sm text-emerald-300" data-testid="verify-ok">
                Verified! Loading your new badge…
              </div>
            )}
            {/* iOS PWA fix: use a <label> wrapping a NOT-hidden input so the tap
                fires the file picker natively. Programmatic click() on a hidden
                input breaks the user-gesture trust chain when the app is
                installed to the home screen — that was the "can't open camera"
                bug. The input is visually invisible via `sr-only` but remains
                the tap target. */}
            <label
              className={`footy-btn-primary w-full mt-6 cursor-pointer ${status === "uploading" || status === "verified" ? "opacity-60 pointer-events-none" : ""}`}
              data-testid="verify-take-selfie"
            >
              {status === "uploading" ? "Checking…" : status === "rejected" ? "Try again" : "Take selfie"}
              <input
                ref={inputRef}
                type="file"
                accept="image/*"
                capture="user"
                className="sr-only"
                onChange={submit}
                disabled={status === "uploading" || status === "verified"}
                data-testid="verify-file-input"
              />
            </label>
            <button onClick={onClose} className="w-full text-slate-400 text-sm mt-3 hover:text-white" data-testid="verify-cancel">Cancel</button>
          </>
        )}
      </div>
    </div>
  );
}


function ProfilePhotoGrid({ photos = [], onRemove, onSetPrimary }) {
  if (!photos.length) {
    return <p className="text-sm text-slate-500">No photos yet. Add up to {MAX_PHOTOS}.</p>;
  }
  return (
    <div className="grid grid-cols-3 gap-2" data-testid="profile-photo-grid">
      {photos.map((url, i) => {
        const isPrimary = i === 0;
        return (
          <div key={`ph-${i}`} className={`relative aspect-[3/4] rounded-2xl overflow-hidden border ${isPrimary ? "border-footy-red/60 ring-2 ring-footy-red/40" : "border-white/10"}`} data-testid={`profile-photo-slot-${i}`}>
            <img src={photoSrc(url)} alt="" className="w-full h-full object-cover" />
            {isPrimary && (
              <span className="absolute top-1.5 left-1.5 inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-footy-red text-white">
                <Star size={9} fill="currentColor" /> Primary
              </span>
            )}
            <button type="button" onClick={()=>onRemove(i)}
              className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-black/70 hover:bg-black text-white flex items-center justify-center"
              data-testid={`profile-photo-remove-${i}`} title="Remove">
              <X size={14} />
            </button>
            {!isPrimary && (
              <button type="button" onClick={()=>onSetPrimary(i)}
                className="absolute bottom-1.5 left-1.5 right-1.5 py-1.5 rounded-full bg-black/70 hover:bg-black text-white text-[10px] font-bold uppercase tracking-widest flex items-center justify-center gap-1"
                data-testid={`profile-photo-set-primary-${i}`}>
                <Star size={10} /> Make primary
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

function NotificationsCard({ user, refresh }) {
  const p = user?.profile || {};
  const [pings, setPings] = useState(p.checkin_pings !== false);
  const [england, setEngland] = useState(!!p.auto_rsvp_england);
  const [digest, setDigest] = useState(p.fellow_fans_digest !== false);
  const [groundsDigest, setGroundsDigest] = useState(p.grounds_recap_digest !== false);
  const [saving, setSaving] = useState(false);
  const [pushOn, setPushOn] = useState(!!p.push_enabled);
  const [pushBusy, setPushBusy] = useState(false);
  const [pushSupported, setPushSupported] = useState(false);
  const [pushPerm, setPushPerm] = useState("default");

  // Keep local state in sync when the profile reloads (e.g. after opt-in from /worldcup)
  useEffect(() => {
    setPings(p.checkin_pings !== false);
    setEngland(!!p.auto_rsvp_england);
    setDigest(p.fellow_fans_digest !== false);
    setGroundsDigest(p.grounds_recap_digest !== false);
    setPushOn(!!p.push_enabled);
  }, [p.checkin_pings, p.auto_rsvp_england, p.fellow_fans_digest, p.grounds_recap_digest, p.push_enabled]);

  useEffect(() => {
    setPushSupported(isPushSupported());
    setPushPerm(currentPermission());
  }, []);

  // Toggle checkin_pings via the profile PUT (piggy-backs on the existing save).
  const togglePings = async (val) => {
    setSaving(true);
    try {
      const body = { ...p, name: user.name, checkin_pings: val };
      await api.put("/profile", body);
      setPings(val);
      await refresh();
      toast.success(val ? "Check-in pings on" : "Check-in pings off");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't save");
    } finally { setSaving(false); }
  };
  const toggleEngland = async (val) => {
    setSaving(true);
    try {
      await api.post("/worldcup/opt-in-england", { opt_in: val });
      setEngland(val);
      await refresh();
      toast.success(val ? "England match-day boost on" : "Opted out");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't save");
    } finally { setSaving(false); }
  };
  const toggleDigest = async (val) => {
    setSaving(true);
    try {
      const body = { ...p, name: user.name, fellow_fans_digest: val };
      await api.put("/profile", body);
      setDigest(val);
      await refresh();
      toast.success(val ? "Monthly digest on" : "Monthly digest off");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't save");
    } finally { setSaving(false); }
  };
  const toggleGroundsDigest = async (val) => {
    setSaving(true);
    try {
      const body = { ...p, name: user.name, grounds_recap_digest: val };
      await api.put("/profile", body);
      setGroundsDigest(val);
      await refresh();
      toast.success(val ? "Grounds recap on" : "Grounds recap off");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't save");
    } finally { setSaving(false); }
  };

  // Push notifications — separate flow from the email digests. Enabling requires
  // both a backend subscribe AND a browser permission prompt.
  const togglePush = async (val) => {
    setPushBusy(true);
    try {
      if (val) {
        await subscribePush();
        setPushOn(true);
        setPushPerm("granted");
        await refresh();
        toast.success("Push notifications on — try Send test to check your device.");
      } else {
        await unsubscribePush();
        setPushOn(false);
        await refresh();
        toast.success("Push notifications off");
      }
    } catch (e) {
      if (e.code === "denied") {
        toast.error("Your browser blocked notifications. Enable them in your browser settings, then try again.");
        setPushPerm("denied");
      } else {
        toast.error(e.message || "Couldn't change push settings");
      }
    } finally { setPushBusy(false); }
  };
  const doTestPush = async () => {
    setPushBusy(true);
    try {
      const res = await sendTestPush();
      if (res.delivered_to > 0) toast.success(`Test sent to ${res.delivered_to} device${res.delivered_to === 1 ? "" : "s"}. Check your notifications.`);
      else toast.info("No active devices yet — enable push on this one first.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't send test");
    } finally { setPushBusy(false); }
  };

  return (
    <div className="mt-6 footy-card p-5" data-testid="notifications-card">
      <div className="flex items-center gap-2 mb-4">
        <Bell size={16} className="text-footy-red" />
        <p className="footy-label !mb-0">Notifications & boosts</p>
      </div>
      <div className="space-y-4">
        {/* Push notifications — appears first so it feels like the marquee toggle */}
        <div data-testid="notif-push-block">
          <label className="flex items-start gap-3 cursor-pointer">
            <div className="flex-1">
              <div className="text-sm font-semibold flex items-center gap-2">
                Push notifications
                <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-footy-red/20 text-footy-red font-black">New</span>
              </div>
              <div className="text-xs text-slate-400 mt-0.5">
                Instant lock-screen alerts for new matches, messages, and RSVPs to your fixtures.
              </div>
            </div>
            <Switch
              data-testid="notif-push-toggle"
              checked={pushOn && pushPerm === "granted"}
              onCheckedChange={togglePush}
              disabled={pushBusy || !pushSupported || pushPerm === "denied"}
            />
          </label>
          {!pushSupported && (
            <p className="text-[11px] text-amber-300/90 mt-2" data-testid="notif-push-unsupported">
              This browser doesn't support push notifications. Add Footymatch to your home screen on iOS 16.4+ or Android Chrome to enable.
            </p>
          )}
          {pushSupported && pushPerm === "denied" && (
            <p className="text-[11px] text-amber-300/90 mt-2" data-testid="notif-push-denied">
              Your browser blocked notifications for this site. Update the site permission and refresh to re-enable.
            </p>
          )}
          {pushOn && pushPerm === "granted" && (
            <button
              type="button"
              onClick={doTestPush}
              disabled={pushBusy}
              className="mt-2 text-[11px] uppercase tracking-widest font-bold text-footy-red hover:text-red-400 disabled:opacity-50"
              data-testid="notif-push-test"
            >
              Send me a test →
            </button>
          )}
        </div>
        <label className="flex items-start gap-3 cursor-pointer">
          <div className="flex-1">
            <div className="text-sm font-semibold">Check-in pings</div>
            <div className="text-xs text-slate-400 mt-0.5">Email me when other Footymatch fans check in to the same match.</div>
          </div>
          <Switch data-testid="notif-pings-toggle" checked={pings} onCheckedChange={togglePings} disabled={saving} />
        </label>
        <label className="flex items-start gap-3 cursor-pointer">
          <div className="flex-1">
            <div className="text-sm font-semibold">Monthly Fellow Fans digest</div>
            <div className="text-xs text-slate-400 mt-0.5">Once a month: how many new {p.club || "fellow"} fans just joined, and what they're here for.</div>
          </div>
          <Switch data-testid="notif-digest-toggle" checked={digest} onCheckedChange={toggleDigest} disabled={saving} />
        </label>
        <label className="flex items-start gap-3 cursor-pointer">
          <div className="flex-1">
            <div className="text-sm font-semibold">Monthly Grounds Book recap</div>
            <div className="text-xs text-slate-400 mt-0.5">Once a month: the grounds you added and your 92 Club progress.</div>
          </div>
          <Switch data-testid="notif-grounds-digest-toggle" checked={groundsDigest} onCheckedChange={toggleGroundsDigest} disabled={saving} />
        </label>
        <label className="flex items-start gap-3 cursor-pointer">
          <div className="flex-1">
            <div className="text-sm font-semibold">England match-day boost</div>
            <div className="text-xs text-slate-400 mt-0.5">Surface my profile higher in Discover when England play in the World Cup.</div>
          </div>
          <Switch data-testid="notif-england-toggle" checked={england} onCheckedChange={toggleEngland} disabled={saving} />
        </label>
      </div>
    </div>
  );
}


// ---------------- Football Passport ----------------
function FootballPassport() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [sharingCard, setSharingCard] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    setError(false);
    api.get("/passport/me")
      .then(r => setData(r.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="mt-6 h-40 rounded-2xl bg-white/5 animate-pulse" data-testid="passport-loading" />;
  }
  // Fallback: don't silently hide the whole section — show a minimal card so users
  // can still reach the QR share page even if /passport/me is momentarily unhappy.
  if (!data || error) {
    const refCode = user?.referral_code || "";
    const shareLink = refCode ? `https://footymatch.co.uk/?ref=${refCode}` : "https://footymatch.co.uk";
    return (
      <div className="mt-6 footy-card p-5" data-testid="football-passport-fallback">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-lg bg-footy-red/15 text-footy-red flex items-center justify-center">
            <Sparkles size={18} />
          </div>
          <div>
            <div className="font-display text-xl uppercase tracking-wider">Football Passport</div>
            <p className="text-xs text-slate-400 mt-0.5">Stats couldn't load — you can still share your invite.</p>
          </div>
        </div>
        <div className="p-3 rounded-xl bg-white/5 border border-white/10">
          <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">Your share link</div>
          <div className="text-xs text-slate-200 break-all font-mono">{shareLink}</div>
        </div>
        <a href="/share" data-testid="passport-open-share-qr"
          className="mt-3 w-full py-2.5 rounded-full text-xs font-bold uppercase tracking-widest bg-footy-red hover:bg-footy-redHover text-white inline-flex items-center justify-center gap-2">
          <QrCode size={13} /> Get my shareable QR code
        </a>
      </div>
    );
  }

  const shareLink = data.user?.referral_code ? `https://footymatch.co.uk/?ref=${data.user.referral_code}` : "https://footymatch.co.uk";
  const clubColorHex = clubColor(data.user?.club);
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      setCopied(true);
      toast.success("Referral link copied");
      setTimeout(() => setCopied(false), 1600);
    } catch {
      toast.error("Copy failed — long-press the link to copy manually");
    }
  };
  const shareLink_ = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Footymatch",
          text: "Join me on Footymatch — find football fans to go to the match with.",
          url: shareLink,
        });
        return;
      } catch { /* user cancelled */ }
    }
    copyLink();
  };

  const sharePassportCard = async () => {
    setSharingCard(true);
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/share/passport?club_color=${encodeURIComponent(clubColorHex)}`;
      const resp = await fetch(url, { credentials: "include" });
      if (!resp.ok) throw new Error("Could not build passport card");
      const blob = await resp.blob();
      const file = new File([blob], "footymatch-passport.png", { type: "image/png" });
      const caption = `Season so far — ${data.away_days} away days · ${data.check_ins} check-ins · ${data.referrals} lads introduced. Find your footy family → ${shareLink}`;
      const shareData = { title: "Footymatch", text: caption, files: [file], url: shareLink };
      if (navigator.canShare && navigator.canShare(shareData)) {
        try { await navigator.share(shareData); toast.success("Shared 🎉"); return; } catch { /* cancelled */ }
      }
      if (navigator.share) {
        try { await navigator.share({ title: "Footymatch", text: caption, url: shareLink }); return; } catch { /* fall through */ }
      }
      const a = document.createElement("a");
      const objectUrl = URL.createObjectURL(blob);
      a.href = objectUrl;
      a.download = "footymatch-passport.png";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
      toast.success("Downloaded — post it to your socials!");
    } catch (e) {
      toast.error(e.message || "Couldn't share");
    } finally { setSharingCard(false); }
  };

  const stats = [
    { icon: Bus, label: "Away days", value: data.away_days, testid: "passport-away" },
    { icon: MapPin, label: "Check-ins", value: data.check_ins, testid: "passport-checkins" },
    { icon: Users, label: "Introduced", value: data.referrals, testid: "passport-referrals" },
    { icon: Heart, label: "Matches", value: data.matches_made, testid: "passport-matches" },
  ];

  return (
    <div className="mt-6 footy-card p-5 relative overflow-hidden" data-testid="football-passport">
      {/* Subtle gold shimmer to make it feel collectable */}
      <div className="absolute inset-0 opacity-10 pointer-events-none" style={{
        background: "radial-gradient(circle at 100% 0%, #d4af37 0%, transparent 60%)"
      }} />
      <div className="relative">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.35em] text-yellow-400">Football passport</p>
            <p className="font-display text-2xl uppercase tracking-tight leading-none mt-1">Your Footymatch career</p>
          </div>
          {data.founding_member && (
            <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full bg-yellow-500/15 text-yellow-300 border border-yellow-500/40" data-testid="badge-founding">
              <Crown size={11} /> Founding
            </span>
          )}
        </div>

        <div className="grid grid-cols-4 gap-2">
          {stats.map(s => {
            const Icon = s.icon;
            return (
              <div key={s.label} className="rounded-xl bg-white/5 border border-white/10 p-3 text-center" data-testid={s.testid}>
                <Icon size={16} className="mx-auto text-footy-red mb-1" />
                <div className="font-display text-2xl leading-none">{s.value ?? 0}</div>
                <div className="text-[9px] text-slate-400 uppercase tracking-widest mt-1">{s.label}</div>
              </div>
            );
          })}
        </div>

        <div className="mt-4 flex items-center gap-3 text-xs text-slate-400">
          <span>Member for <span className="text-white font-semibold">{data.days_active} day{data.days_active === 1 ? "" : "s"}</span></span>
          {data.say_hi_count > 0 && (
            <span>· <span className="text-white font-semibold">{data.say_hi_count}</span> matchday hello{data.say_hi_count === 1 ? "" : "s"}</span>
          )}
        </div>

        {/* Referral card */}
        <div className="mt-4 rounded-xl bg-white/5 border border-white/10 p-3" data-testid="passport-referral-card">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-yellow-500/15 text-yellow-400 flex items-center justify-center flex-none">
              <Share2 size={16} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-sm">Bring the lads</div>
              <p className="text-xs text-slate-400 mt-0.5">Every sign-up through your link gets counted on your passport.</p>
              <div className="mt-2 flex items-center gap-2">
                <code className="flex-1 min-w-0 truncate text-[11px] font-mono px-2 py-1.5 rounded-md bg-black/40 border border-white/10 text-slate-300" data-testid="passport-ref-link">{shareLink}</code>
                <button onClick={copyLink} className="text-slate-400 hover:text-white p-1.5" title="Copy" data-testid="passport-copy-btn">
                  <Copy size={14} />
                </button>
                <button onClick={shareLink_} className="text-[10px] font-bold uppercase tracking-widest px-3 py-1.5 rounded-full bg-footy-red hover:bg-footy-redHover text-white flex-none" data-testid="passport-share-btn">
                  Share
                </button>
              </div>
              {copied && <p className="text-[10px] text-emerald-400 mt-1.5" data-testid="passport-copied-toast">Copied to clipboard</p>}
            </div>
          </div>
        </div>

        {/* Share the whole passport as an image */}
        <button onClick={sharePassportCard} disabled={sharingCard}
          className="mt-3 w-full py-2.5 rounded-full text-xs font-bold uppercase tracking-widest border border-yellow-500/40 text-yellow-300 hover:bg-yellow-500/10 inline-flex items-center justify-center gap-2 disabled:opacity-50"
          data-testid="passport-share-card-btn">
          <Share2 size={13} /> {sharingCard ? "Preparing…" : "Share my passport card"}
        </button>

        {/* Full QR share page — dedicated poster-ready QR */}
        <a href="/share" data-testid="passport-open-share-qr"
          className="mt-2 w-full py-2.5 rounded-full text-xs font-bold uppercase tracking-widest border border-white/20 text-slate-200 hover:bg-white/5 inline-flex items-center justify-center gap-2">
          <QrCode size={13} /> Get my shareable QR code
        </a>
      </div>
    </div>
  );
}



// ---------------- Grounds Book preview card ----------------
function GroundsBookCard() {
  const nav = useNavigate();
  const [state, setState] = useState({ loading: true, count: 0, visits: [] });
  const [crests, setCrests] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const [gRes, cRes] = await Promise.all([
          api.get("/grounds/me"),
          api.get("/grounds/crests"),
        ]);
        setState({ loading: false, count: gRes.data.count || 0, visits: gRes.data.visits || [] });
        setCrests(cRes.data || {});
      } catch {
        setState(s => ({ ...s, loading: false }));
      }
    })();
  }, []);

  const preview = state.visits.slice(0, 5);
  const initials = (name) => (name || "?").split(" ").filter(Boolean).map(w => w[0]).slice(0,2).join("").toUpperCase();

  return (
    <button
      type="button"
      onClick={() => nav("/grounds")}
      className="mt-6 w-full text-left footy-card p-5 relative overflow-hidden hover:border-footy-red/40 transition-colors"
      data-testid="grounds-book-card"
    >
      <div className="absolute -right-4 -top-4 opacity-[0.08] pointer-events-none">
        <Trophy size={140} className="text-yellow-400" />
      </div>
      <div className="relative flex items-start justify-between">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.35em] text-yellow-400">Grounds Book</p>
          <p className="font-display text-2xl uppercase tracking-tight leading-none mt-1">
            {state.loading ? "Loading…" : state.count === 0 ? "Start ticking off the 92" : `${state.count} ground${state.count === 1 ? "" : "s"} ticked`}
          </p>
          <p className="text-xs text-slate-400 mt-1.5">
            {state.count === 0 ? "Add every ground you've ever been to — private log, shareable link." : "Tap to add more or share your book."}
          </p>
        </div>
        <ChevronRight size={20} className="text-slate-400 flex-none mt-1" />
      </div>

      {preview.length > 0 && (
        <div className="mt-4 flex items-center gap-2" data-testid="grounds-book-preview">
          {preview.map(v => {
            const color = CLUBS.find(c => c.name === v.club_name)?.color || "#152A55";
            const crest = crests[v.club_name.toLowerCase()];
            return (
              <div key={v.club_name} className="w-11 h-11 rounded-full bg-white/95 flex items-center justify-center overflow-hidden border-2 border-footy-bg" title={v.club_name}>
                {crest ? (
                  <img src={crest} alt={v.club_name} className="w-9 h-9 object-contain" />
                ) : (
                  <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-[10px] font-bold" style={{ background: color }}>
                    {initials(v.club_name)}
                  </div>
                )}
              </div>
            );
          })}
          {state.count > preview.length && (
            <span className="text-xs text-slate-400 font-bold">+{state.count - preview.length}</span>
          )}
        </div>
      )}
    </button>
  );
}

// ---------------- Verify social-proof strip ----------------
// "4 Arsenal fans verified this week — join them." Shown inside the Verification
// card for unverified users only. Silent no-op if the endpoint fails or there
// have been zero verifications this week (never render an empty proof line).
function VerifySocialProof() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/verify/social-proof")
      .then(r => setData(r.data))
      .catch(() => setData(null));
  }, []);
  if (!data) return null;
  const club = data.club || "";
  const clubCount = data.club_this_week || 0;
  const totalCount = data.total_this_week || 0;
  // Prefer the local (same-club) framing when we have any. Fall back to global.
  let line = null;
  if (club && clubCount > 0) {
    line = <>You + <strong className="text-white">{clubCount}</strong> {club} fan{clubCount === 1 ? "" : "s"} verified this week — join them.</>;
  } else if (totalCount > 0) {
    line = <>You + <strong className="text-white">{totalCount}</strong> fan{totalCount === 1 ? "" : "s"} verified this week — join them.</>;
  }
  if (!line) return null;
  return (
    <div className="mt-4 rounded-xl p-3 bg-emerald-500/10 border border-emerald-500/25 flex items-center gap-2.5 text-sm text-emerald-100" data-testid="verify-social-proof">
      <div className="w-8 h-8 rounded-full bg-emerald-400/20 text-emerald-300 flex items-center justify-center flex-none">
        <ShieldCheck size={14} />
      </div>
      <p className="text-xs leading-snug" data-testid="verify-social-proof-line">{line}</p>
    </div>
  );
}

